<template>
  <InfusePageLayout>
    <div class="flex flex-col gap-6 overflow-y-auto">
      <UsersTable
        :rows="users"
        :active="(row, _) => route?.params?.id?.toString() === row.id"
        :loading="isLoading || isDeletingUser"
        :page="currentPage"
        :searchEnabled="true"
        :searchQuery="searchQuery"
        :filters="filterCollection.filters"
        :filterConfigs="filterConfigs"
        :key="actionsCompleted"
        :has-more-items="hasNextPage"
        :removable="true"
        :editable="true"
        @click-row="redirectToUser"
        @load="fetchNextPage"
        @search="search($event)"
        @create="showCreateUserModal = true"
        @move="showMoveUserModal = true"
        @remove="
          (row: User) => {
            activeUser = row;
            showDeleteUserModal = true;
          }
        "
        @edit="
          (row: User) => {
            activeUser = row;
            showUpdateUserModal = true;
          }
        "
      />
    </div>
  </InfusePageLayout>

  <!-- CREATE -->
  <MutationUserModal
    v-if="showCreateUserModal"
    @close="showCreateUserModal = false"
    @create="onCreateUser"
    :loading="isCreatingUser"
    :navigation-level="level"
  />

  <!-- UPDATE -->
  <MutationUserModal
    v-if="showUpdateUserModal && activeUser"
    :user="activeUser"
    :loading="isUpdatingUser"
    @close="closeUpdateModal"
    @update="onUpdateUser"
    :navigation-level="level"
  />

  <InfuseConfirmModal
    v-if="showDeleteUserModal"
    :action-title="t('iam.users.header.delete_user')"
    :action-description="t('iam.users.info.delete_user', { user: activeUser?.email || '' })"
    verification-text="delete"
    @confirm="onDeleteUser"
    @close="closeDeleteModal"
  />

  <MoveUserModal
    v-if="showMoveUserModal"
    :current-level="level"
    :current-organisation-id="user?.organisationId"
    :current-tenant-id="user?.tenantId"
    :loading="isMovingUsers"
    @close="showMoveUserModal = false"
    @move="onMoveUsers"
  />
</template>

<script setup lang="ts">
import { CreateUser, UpdateUser, User, UserLevel, UserMoveDestinationType } from '@infuse/iam/user/type';
import { FilterCollection } from '@infuse/cpms/charging-station/filters';
import { useCreateUser, useDeleteUser, useUpdateUser, useMoveUsers } from '@infuse/iam/user/mutations';
import { useUsers } from '@infuse/iam/user/queries';
import { MutationUserModal, MoveUserModal, UsersTable } from '@infuse/iam/user/ui';
import { InfuseConfirmModal } from '@infuse/ui/components';
import { InfusePageLayout } from '@infuse/ui/layouts';
import { ref, watch, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useCurrentUserStore } from '@infuse/iam/user/store';
import * as Sentry from '@sentry/vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const filterCollection = new FilterCollection();
const filterConfigs = ref();

const activeUser = ref<User | undefined>(undefined);

const { level, user } = useCurrentUserStore();

const { users, currentPage, isLoading, hasNextPage, fetchNextPage, searchQuery, refetch } = useUsers();

const { mutateAsync: createUser, isPending: isCreatingUser } = useCreateUser();
const { mutateAsync: updateUser, isPending: isUpdatingUser } = useUpdateUser();
const { mutateAsync: deleteUser, isPending: isDeletingUser } = useDeleteUser();
const { mutateAsync: moveUsers, isPending: isMovingUsers } = useMoveUsers();

const actionsCompleted = ref(0); // to force re-render of the table when an action is completed

const showCreateUserModal = ref(false);
const showUpdateUserModal = ref(false);
const showDeleteUserModal = ref(false);
const showMoveUserModal = ref(false);

watch(
  () => route.query.search,
  (search) => {
    searchQuery.value = search?.toString();
  },
  { immediate: true }
);

async function search(search: string): Promise<void> {
  searchQuery.value = search;
  replaceQueryParams(searchQuery.value);
}

function replaceQueryParams(searchQuery: string | undefined) {
  if (router.currentRoute.value.name !== 'user') {
    // only do this if the user is currently on this page and not some sub-route.
    return;
  }

  router.replace({
    name: 'user',
    query: {
      search: searchQuery || undefined
    }
  });
}

function redirectToUser(row: User): void {
  let routeName = 'user.details';

  // If you're on a specific sub-route (tab), and click on another charging
  // station in the table, open the same tab for that new charging station.
  if (router.currentRoute.value.name !== 'user') {
    routeName = router.currentRoute.value.name?.toString() || routeName;
  }

  router.push({
    name: routeName,
    params: { id: row.id }
  });
}

async function onCreateUser(vars: { user: CreateUser }): Promise<void> {
  await createUser({ user: vars.user });
  showCreateUserModal.value = false;
  refetch();
}

async function onDeleteUser(): Promise<void> {
  if (!activeUser.value) {
    return;
  }

  await deleteUser({ id: activeUser.value.id });
  closeDeleteModal();
  refetch();
}

function closeDeleteModal() {
  showDeleteUserModal.value = false;
  activeUser.value = undefined;
}

async function onUpdateUser(vars: { id: string; user: UpdateUser }): Promise<void> {
  // UpdateUser includes 'version' field for optimistic locking
  await updateUser({ id: vars.id, user: vars.user });
  closeUpdateModal();
  refetch();
}

function closeUpdateModal() {
  showUpdateUserModal.value = false;
  activeUser.value = undefined;
}

async function onMoveUsers(data: {
  users: User[];
  destinationType: UserMoveDestinationType;
  destinationId?: string;
  targetTenantId?: string;
}) {
  try {
    await moveUsers({
      users: data.users,
      destinationType: data.destinationType,
      destinationId: data.destinationId,
      targetTenantId: data.targetTenantId
    });

    showMoveUserModal.value = false;
    refetch(); // Refresh the user list
  } catch (error) {
    console.error('Error moving users:', error);
  }
}
</script>
